/**
 * Utility to move an item from one paginated query to another
 * @param {Object} options
 * @param {import("@tanstack/react-query").QueryClient} options.queryClient - React Query client
 * @param {Array} options.queries - Array of all queries to search through
 * @param {Function} options.sourceFilter - Function to identify the source query (q) => boolean
 * @param {Function} options.targetFilter - Function to identify the target query (q) => boolean
 * @param {Function} options.itemMatcher - Function to find the item (item) => boolean
 * @param {Function} options.itemUpdater - Function to update the item (item) => updatedItem
 * @returns {Object|null} - The found item or null
 */
export const moveBetweenPaginatedQueries = ({
  queryClient,
  queries,
  sourceFilter,
  targetFilter,
  itemMatcher,
  itemUpdater
}) => {
  const sourceQuery = queries.find(sourceFilter)
  const sourceData = sourceQuery?.state?.data

  const item = sourceData?.pages?.flatMap((p) => p.data || []).find(itemMatcher)

  if (!item) {
    return null
  }

  const updatedItem = itemUpdater(item)

  queries.forEach((query) => {
    const data = query.state.data
    if (!data?.pages) return

    const isSource = sourceFilter(query)
    const isTarget = targetFilter(query)

    if (isSource) {
      queryClient.setQueryData(query.queryKey, {
        ...data,
        pages: data.pages.map((page, index) => ({
          ...page,
          data: page.data?.filter((item) => !itemMatcher(item)) || [],
          meta:
            index === 0 && page.meta
              ? {
                  ...page.meta,
                  total_records: (page.meta.total_records || 1) - 1
                }
              : page.meta
        }))
      })
    }

    if (isTarget) {
      queryClient.setQueryData(query.queryKey, {
        ...data,
        pages: data.pages.map((page, index) => ({
          ...page,
          data: index === 0 ? [updatedItem, ...(page.data || [])] : page.data,
          meta:
            index === 0 && page.meta
              ? {
                  ...page.meta,
                  total_records: (page.meta.total_records || 0) + 1
                }
              : page.meta
        }))
      })
    }
  })

  return item
}
